import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ApiService from '../services/api';

interface SurveyQuestion {
  id: number;
  questionText: string;
  questionType: 'rating' | 'text' | 'multiple_choice';
  displayOrder: number;
}

interface Survey {
  id: number;
  customerName: string;
  expiresAt: string;
}

const Survey: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [questions, setQuestions] = useState<SurveyQuestion[]>([]);
  const [responses, setResponses] = useState<{ [key: number]: any }>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (token) {
      loadSurvey();
    }
  }, [token]);

  const loadSurvey = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getSurvey(token!);
      
      if (response.success) {
        setSurvey(response.survey);
        setQuestions(response.questions);
      } else {
        setError(response.message || 'Anket yüklenirken hata oluştu');
      }
    } catch (error: any) {
      setError(error.message || 'Anket yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleRatingChange = (questionId: number, rating: number) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: { rating }
    }));
  };

  const handleTextChange = (questionId: number, text: string) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: { answer: text }
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Tüm soruların cevaplandığını kontrol et
    const unansweredQuestions = questions.filter(q => !responses[q.id]);
    if (unansweredQuestions.length > 0) {
      setError('Lütfen tüm soruları cevaplayın');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const responseData = questions.map(q => ({
        questionId: q.id,
        rating: responses[q.id]?.rating,
        answer: responses[q.id]?.answer
      }));

      const response = await ApiService.submitSurvey(token!, responseData);
      
      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        setError(response.message || 'Anket gönderilirken hata oluştu');
      }
    } catch (error: any) {
      setError(error.message || 'Anket gönderilirken hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Anket yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Hata</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600 transition-colors"
          >
            Ana Sayfaya Dön
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <div className="text-green-500 text-6xl mb-4">✅</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Teşekkürler!</h2>
          <p className="text-gray-600 mb-6">
            Anketinizi başarıyla tamamladınız. Geri bildiriminiz bizim için çok değerli.
          </p>
          <p className="text-sm text-gray-500">
            Ana sayfaya yönlendiriliyorsunuz...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Memnuniyet Anketi
            </h1>
            <p className="text-gray-600">
              Sayın {survey?.customerName}, deneyiminizi değerlendirmek için kısa bir anket doldurmanızı rica ediyoruz.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {questions.map((question, index) => (
              <div key={question.id} className="border-b border-gray-200 pb-6">
                <h3 className="text-lg font-medium text-gray-800 mb-4">
                  {index + 1}. {question.questionText}
                </h3>

                {question.questionType === 'rating' && (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Çok Kötü</span>
                      <span className="text-sm text-gray-500">Çok İyi</span>
                    </div>
                    <div className="flex justify-center space-x-2">
                      {[1, 2, 3, 4, 5].map((rating) => (
                        <button
                          key={rating}
                          type="button"
                          onClick={() => handleRatingChange(question.id, rating)}
                          className={`w-12 h-12 rounded-full border-2 transition-all duration-200 ${
                            responses[question.id]?.rating === rating
                              ? 'bg-blue-500 border-blue-500 text-white'
                              : 'border-gray-300 hover:border-blue-300 hover:bg-blue-50'
                          }`}
                        >
                          {rating}
                        </button>
                      ))}
                    </div>
                    <div className="text-center text-sm text-gray-500">
                      {responses[question.id]?.rating && (
                        <span>
                          Seçiminiz: {responses[question.id].rating}/5
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {question.questionType === 'text' && (
                  <textarea
                    value={responses[question.id]?.answer || ''}
                    onChange={(e) => handleTextChange(question.id, e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    rows={4}
                    placeholder="Yorumunuzu buraya yazın..."
                  />
                )}
              </div>
            ))}

            <div className="pt-6">
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-blue-500 text-white py-3 px-6 rounded-md hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Gönderiliyor...' : 'Anketi Gönder'}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center text-sm text-gray-500">
            <p>Bu anket {new Date(survey?.expiresAt || '').toLocaleDateString('tr-TR')} tarihine kadar geçerlidir.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Survey;
